import { useEffect, useState } from 'react'
import { ColorField } from '@/components/config/ColorField'
import { DESIGN_PRESETS } from '@/utils/designPresets'
import { getPublicSystemBranding, poweredByHref, STITUFY_HOME_URL } from '@/services/api/systemBranding'

export interface BrandingConfig {
  logo_position: string
  show_logo: boolean
  bg_color: string
  text_color: string
  heading_color: string
  button_bg_color: string
  button_text_color: string
  button_hover_color: string
  link_color: string
  border_color: string
  layout_style: string
}

/** Map the design stem config (design_* keys) to email branding fields. */
export function stemToBranding(design: Record<string, unknown> | null): BrandingConfig {
  const d = (key: string, fallback: string) => {
    const v = design?.[key]
    return typeof v === 'string' && v.startsWith('#') && v.length === 7 ? v : fallback
  }
  return {
    logo_position: typeof design?.design_logo_position === 'string' ? String(design.design_logo_position) : 'center',
    show_logo: design?.design_show_school_name !== false,
    bg_color: d('design_bg_color', '#f9fafb'),
    text_color: d('design_text_color', '#111827'),
    heading_color: d('design_text_color', '#111827'),
    button_bg_color: d('design_button_bg_color', '#2563eb'),
    button_text_color: d('design_button_text_color', '#ffffff'),
    button_hover_color: d('design_button_hover_color', '#1d4ed8'),
    link_color: d('design_primary_color', '#2563eb'),
    border_color: d('design_border_color', '#d1d5db'),
    layout_style: 'default',
  }
}

interface EmailBrandingEditorProps {
  brandingConfig: BrandingConfig | null
  brandingSource: 'school_design' | 'custom'
  schoolDesign?: Record<string, unknown> | null
  onChange: (config: BrandingConfig) => void
  onSourceChange: (source: 'school_design' | 'custom') => void
}

const DEFAULT_BRANDING: BrandingConfig = {
  logo_position: 'center',
  show_logo: true,
  bg_color: '#f9fafb',
  text_color: '#111827',
  heading_color: '#111827',
  button_bg_color: '#2563eb',
  button_text_color: '#ffffff',
  button_hover_color: '#1d4ed8',
  link_color: '#2563eb',
  border_color: '#d1d5db',
  layout_style: 'default',
}

export function EmailBrandingEditor({ brandingConfig, brandingSource, schoolDesign, onChange, onSourceChange }: EmailBrandingEditorProps) {
  const config = { ...DEFAULT_BRANDING, ...(brandingConfig || {}) }
  const isSchoolDesign = brandingSource === 'school_design'
  const preview = isSchoolDesign ? stemToBranding(schoolDesign ?? null) : config
  const schoolLogo = typeof schoolDesign?.design_logo_data === 'string' ? schoolDesign.design_logo_data : ''
  const [systemLogo, setSystemLogo] = useState('')
  const [systemLogoUrl, setSystemLogoUrl] = useState(STITUFY_HOME_URL)

  useEffect(() => {
    let alive = true
    getPublicSystemBranding()
      .then(b => {
        if (!alive) return
        setSystemLogo(b.email_logo_png || b.email_logo_svg || '')
        setSystemLogoUrl(poweredByHref(b.url))
      })
      .catch(() => { /* public branding optional */ })
    return () => { alive = false }
  }, [])

  const update = (key: keyof BrandingConfig, value: string | boolean) => {
    onChange({ ...config, [key]: value })
  }

  /** Umschalten auf «Eigenes Design»: mit dem aktuellen Schul-Design vorbelegen
   *  statt mit den Blau-Defaults (Startpunkt = geerbter Zustand). */
  const switchToCustom = () => {
    onSourceChange('custom')
    onChange(stemToBranding(schoolDesign ?? null))
  }

  const applyPreset = (idx: number) => {
    const preset = DESIGN_PRESETS[idx]
    if (!preset) return
    onChange({
      ...config,
      bg_color: preset.values.design_bg_color,
      text_color: preset.values.design_text_color,
      heading_color: preset.values.design_text_color,
      button_bg_color: preset.values.design_button_bg_color,
      button_text_color: preset.values.design_button_text_color,
      button_hover_color: preset.values.design_button_hover_color,
      link_color: preset.values.design_primary_color,
      border_color: preset.values.design_border_color,
    })
  }

  return (
    <div className="space-y-6">
      {/* Branding Source Toggle */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="branding_source"
            checked={isSchoolDesign}
            onChange={() => onSourceChange('school_design')}
            className="rounded"
          />
          <span className="text-sm">Schul-Design (erbt)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="branding_source"
            checked={!isSchoolDesign}
            onChange={switchToCustom}
            className="rounded"
          />
          <span className="text-sm">Eigenes Design</span>
        </label>
      </div>

      {isSchoolDesign && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          Farben und Logo werden automatisch aus dem Schul-Design übernommen. Aktiviere &quot;Eigenes Design&quot; um die E-Mail-Vorlage zu übersteuern.
        </div>
      )}

      {/* Presets (only for custom) */}
      {!isSchoolDesign && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Farb-Preset</label>
          <select
            onChange={(e) => e.target.value !== '' && applyPreset(Number(e.target.value))}
            defaultValue=""
            className="w-full px-3 py-2 border rounded-md text-sm"
          >
            <option value="" disabled>Farb-Preset wählen...</option>
            {DESIGN_PRESETS.map((p, i) => <option key={p.key} value={i}>{p.label}</option>)}
          </select>
        </div>
      )}

      {/* Logo (only for custom) */}
      {!isSchoolDesign && (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <h4 className="font-medium text-gray-900">Logo</h4>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.show_logo}
                onChange={(e) => update('show_logo', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Logo anzeigen</span>
            </label>
          </div>
          {config.show_logo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
              <select
                value={config.logo_position}
                onChange={(e) => update('logo_position', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="center">Zentriert</option>
                <option value="left">Links</option>
                <option value="right">Rechts</option>
                <option value="none">Verbergen</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Colors (only for custom) */}
      {!isSchoolDesign && (
        <div className="bg-white rounded-lg shadow p-4 space-y-2">
          <h4 className="font-medium text-gray-900">Farben</h4>
          <ColorField label="Hintergrund" value={config.bg_color} onChange={(v) => update('bg_color', v)} />
          <ColorField label="Text" value={config.text_color} onChange={(v) => update('text_color', v)} />
          <ColorField label="Überschrift" value={config.heading_color} onChange={(v) => update('heading_color', v)} />
          <ColorField label="Button" value={config.button_bg_color} onChange={(v) => update('button_bg_color', v)} />
          <ColorField label="Button Text" value={config.button_text_color} onChange={(v) => update('button_text_color', v)} />
          <ColorField label="Button Hover" value={config.button_hover_color} onChange={(v) => update('button_hover_color', v)} />
          <ColorField label="Link" value={config.link_color} onChange={(v) => update('link_color', v)} />
          <ColorField label="Rahmen" value={config.border_color} onChange={(v) => update('border_color', v)} />
        </div>
      )}

      {/* Layout (only for custom) */}
      {!isSchoolDesign && (
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <h4 className="font-medium text-gray-900">Layout</h4>
          <div className="grid grid-cols-3 gap-3">
            {(['default', 'card', 'minimal'] as const).map(ls => (
              <button
                key={ls}
                onClick={() => update('layout_style', ls)}
                className={`p-3 border-2 rounded-lg text-center text-sm transition ${
                  config.layout_style === ls
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {ls === 'default' ? 'Standard' : ls === 'card' ? 'Karte' : 'Minimal'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <h4 className="font-medium text-gray-900">Vorschau</h4>
        <div className="rounded-lg overflow-hidden border border-gray-200 text-sm" style={{ backgroundColor: preview.bg_color }}>
          <div className="max-w-[560px] mx-auto bg-white overflow-hidden" style={{ borderRadius: 8 }}>
            <div
              style={{
                padding: 16,
                backgroundColor: preview.bg_color,
                borderBottom: `2px solid ${preview.button_bg_color}`,
                textAlign: preview.logo_position === 'left' ? 'left' : 'center',
              }}
            >
              {preview.show_logo && schoolLogo && preview.logo_position !== 'none' && (
                <img src={schoolLogo} alt="Schullogo" style={{ maxHeight: 48, maxWidth: 160, marginBottom: 6 }} />
              )}
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', fontWeight: 600 }}>
                Schulname
              </div>
              <h3 style={{ color: preview.heading_color, fontSize: 18, fontWeight: 700, margin: '4px 0' }}>
                Beispiel-Überschrift
              </h3>
            </div>
            <div style={{ padding: 16, color: preview.text_color }}>
              <p style={{ marginBottom: 12 }}>Dies ist ein Beispieltext für die E-Mail-Vorschau.</p>
              <a
                href="#preview"
                onClick={e => e.preventDefault()}
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: preview.button_bg_color,
                  color: preview.button_text_color,
                  textDecoration: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Button
              </a>
            </div>
            <div style={{ padding: '8px 16px', backgroundColor: preview.bg_color, borderTop: `1px solid ${preview.border_color}`, textAlign: 'center' }}>
              {systemLogo && (
                <a href={systemLogoUrl} target="_blank" rel="noopener noreferrer">
                  <img src={systemLogo} alt="" style={{ height: 32, maxWidth: 140, margin: '0 auto 8px', display: 'block' }} />
                </a>
              )}
              <p style={{ fontSize: 10, color: '#94a3b8', margin: 0 }}>
                Diese E-Mail wurde automatisch generiert. Bitte antworten Sie nicht darauf.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
